import {
  planOfferAdd,
  type CatalogItem,
  type Offer,
  type Store,
  type SyncChange,
  type TripItem,
  type TripSummary,
} from '@sp/shared'
import { api } from './api.ts'
import { newId } from './storage.ts'
import { offerOriginal, offerTitle } from './offers.ts'

/** Rozpracovane nakupy od nejblizsiho data; bez data (bezne) az za nimi. */
export function openTrips(trips: readonly TripSummary[]): TripSummary[] {
  return trips
    .filter((t) => t.status !== 'done')
    .sort(
      (a, b) =>
        (a.plannedDate ?? '9999').localeCompare(b.plannedDate ?? '9999') || b.createdAt.localeCompare(a.createdAt),
    )
}

export interface OfferPick {
  offer: Offer
  /** Polozka sablony, ke ktere akce patri; null = akce sama o sobe. */
  catalogItem: CatalogItem | null
}

/** Kam akce na nakupu pujde: obchod na trase, obchod skupiny pridany na trasu, nebo kdekoli. */
export interface OfferPlace {
  store: Store | null
  /** Obchod na trase jeste neni — prida se. */
  addsStore: boolean
}

/**
 * Obchod pro akci na nakupu. Nakup „kdekoli" nic neresi; jinak obchod retezce
 * na trase, a kdyz tam neni, obchod skupiny s tim retezcem (prida se na trasu).
 * null = akce na tenhle nakup nepatri (skupina obchod toho retezce nema).
 */
export function offerPlace(offer: Offer, trip: Pick<TripSummary, 'storeIds' | 'kind'>, stores: readonly Store[]): OfferPlace | null {
  const route = trip.storeIds.flatMap((id) => stores.find((s) => s.id === id) ?? [])
  if (route.length === 0) return { store: null, addsStore: false }
  const onRoute = route.find((s) => s.offerChain === offer.chain)
  if (onRoute) return { store: onRoute, addsStore: false }
  const other = stores.find((s) => !s.archived && s.offerChain === offer.chain)
  return other ? { store: other, addsStore: true } : null
}

export interface OffersAdded {
  added: number
  /** Polozka sablony uz na nakupu byla — vzala si cenu (a obchod) akce. */
  updated: TripItem[]
  /** Polozka sablony uz je koupena, nic se nezmenilo. */
  inBasket: TripItem[]
  routeAdded: Store[]
}

/**
 * Akce na nakup primo pres API (stranka Akce a sablona nemaji frontu nakupu).
 * Polozka sablony, ktera uz na nakupu je, nepribude podruhe — presune se
 * k akci a vezme si jeji cenu (planOfferAdd).
 */
export async function addOffersToTrip(
  tripId: string,
  picks: readonly OfferPick[],
  stores: readonly Store[],
): Promise<OffersAdded> {
  const trip = await api.trips.get(tripId)
  const result: OffersAdded = { added: 0, updated: [], inBasket: [], routeAdded: [] }
  const changedAt = new Date().toISOString()
  const changes: SyncChange[] = []
  const places = picks.map((pick) => offerPlace(pick.offer, trip, stores))

  for (const place of places) {
    if (place?.addsStore && place.store && !result.routeAdded.some((s) => s.id === place.store?.id)) {
      result.routeAdded.push(place.store)
    }
  }
  if (result.routeAdded.length > 0) {
    await api.trips.update(trip.id, { storeIds: [...trip.storeIds, ...result.routeAdded.map((s) => s.id)] })
  }

  picks.forEach((pick, index) => {
    const place = places[index]
    if (!place) return
    const { offer, catalogItem } = pick
    const storeId = place.store?.id ?? null
    if (catalogItem) {
      const plan = planOfferAdd(trip.items, {
        catalogItemId: catalogItem.id,
        storeId,
        priceMinor: offer.priceMinor,
        currency: offer.currency,
        name: offer.name,
        offer,
      })
      if (plan.kind === 'in_basket') {
        result.inBasket.push(plan.item)
        return
      }
      if (plan.kind === 'update') {
        if (Object.keys(plan.patch).length > 0) {
          changes.push({ op: 'patch', itemId: plan.item.id, patch: plan.patch, changedAt })
        }
        result.updated.push(plan.item)
        return
      }
    }
    changes.push({
      op: 'add',
      changedAt,
      item: {
        id: newId(),
        // S polozkou sablony nebo s prekladem zustava cesky nazev a nazev
        // z letaku jde do poznamky — podle nej se v regalu hleda.
        name: catalogItem?.name ?? offerTitle(offer),
        catalogItemId: catalogItem?.id ?? null,
        storeId,
        qty: catalogItem?.defaultQty ?? 1,
        unit: catalogItem?.unit ?? null,
        expectedPriceMinor: offer.priceMinor,
        expectedCurrency: offer.currency,
        note: catalogItem || offerOriginal(offer) ? offer.name.slice(0, 500) : offer.brand,
        source: 'leaflet',
        deal:
          offer.priceMinor === null
            ? null
            : {
                offerId: offer.id,
                name: offer.name.slice(0, 500),
                validTo: offer.validTo,
                requiresCard: offer.requiresCard,
                prev: { priceMinor: null, currency: null, storeId },
              },
      },
    })
    result.added += 1
  })
  if (changes.length > 0) await api.trips.sync(trip.id, changes)
  return result
}

/** Akce plati v den nakupu (nakup bez data = dnes); bez platnosti plati vzdy. */
function offerValidOn(offer: Offer, date: string | null): boolean {
  const day = date ?? new Date().toLocaleDateString('sv-SE')
  const start = Date.parse(`${day}T00:00:00`)
  const end = start + 24 * 60 * 60 * 1000
  return (!offer.validFrom || Date.parse(offer.validFrom) < end) && (!offer.validTo || Date.parse(offer.validTo) >= start)
}

/**
 * „Patří k mé položce" na strance Akce: akce se rovnou pouzije u polozky na
 * rozpracovanych nakupech, kde uz je, akce v den nakupu plati a obchod je na
 * trase (Richard 2026-10-13). Nic nepridava — polozka ani obchod na trasu.
 */
export async function applyOfferToTrips(
  offer: Offer,
  catalogItem: CatalogItem,
  trips: readonly TripSummary[],
  stores: readonly Store[],
): Promise<TripSummary[]> {
  const applied: TripSummary[] = []
  for (const summary of openTrips(trips)) {
    const place = offerPlace(offer, summary, stores)
    if (!place || place.addsStore || !offerValidOn(offer, summary.plannedDate)) continue
    const trip = await api.trips.get(summary.id)
    const plan = planOfferAdd(trip.items, {
      catalogItemId: catalogItem.id,
      storeId: place.store?.id ?? null,
      priceMinor: offer.priceMinor,
      currency: offer.currency,
      name: offer.name,
      offer,
    })
    if (plan.kind !== 'update' || plan.item.deal?.offerId === offer.id || Object.keys(plan.patch).length === 0) continue
    await api.trips.sync(trip.id, [{ op: 'patch', itemId: plan.item.id, patch: plan.patch, changedAt: new Date().toISOString() }])
    applied.push(summary)
  }
  return applied
}
